import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

/**
 * Shared physically based lab stage for Chemistry 3D scenes (chemistry/CLAUDE.md "Interactive
 * calculation standard" item 6). Every scene gets the same environment lighting, ACES tone
 * mapping, shadows, white bench and token gradient background, plus reviewed glassware parts.
 * Values here were tuned against screenshots of the water beaker; change them only with a
 * screenshot check of every scene that uses them.
 */
export interface LabPalette {
  liquid: THREE.Color;
  ice: THREE.Color;
  glass: THREE.Color;
  steam: THREE.Color;
  heat: THREE.Color;
  glow: THREE.Color;
  metal: THREE.Color;
  bench: THREE.Color;
  accent: THREE.Color;
}

/** Reads the `--lab-*` token colours set by `.lab-scene-stage` in `index.css`. */
export function readLabPalette(element: HTMLElement): LabPalette {
  const styles = window.getComputedStyle(element);
  const color = (name: string) => new THREE.Color(styles.getPropertyValue(name).trim() || '#808080');
  return {
    liquid: color('--lab-liquid'),
    ice: color('--lab-ice'),
    glass: color('--lab-glass'),
    steam: color('--lab-steam'),
    heat: color('--lab-heat'),
    glow: color('--lab-glow'),
    metal: color('--lab-metal'),
    bench: color('--lab-bench'),
    accent: color('--lab-accent'),
  };
}

/** What a scene module returns to `LabCanvas3D`. */
export interface LabSceneHandle<S> {
  update: (state: S, elapsedS: number, playing: boolean) => void;
  resize: (width: number, height: number) => void;
  dispose: () => void;
}

export type LabSceneFactory<S> = (renderer: THREE.WebGLRenderer, palette: LabPalette, reducedMotion: boolean) => LabSceneHandle<S>;

export interface LabStage {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  renderer: THREE.WebGLRenderer;
  palette: LabPalette;
  benchY: number;
  /** Registers a geometry, material or texture for disposal and returns it. */
  keep: <T extends { dispose: () => void }>(item: T) => T;
  /** Call once after the apparatus is built: fog only blends the far bench into the background. */
  finish: () => void;
  render: (playing: boolean) => void;
  resize: (width: number, height: number) => void;
  dispose: () => void;
}

/** Deterministic 0–1 value per index, so a scene looks the same on every load. */
export function seeded(index: number, salt: number): number {
  const value = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453;
  return value - Math.floor(value);
}

function canvasTexture(width: number, height: number, draw: (context: CanvasRenderingContext2D) => void): THREE.CanvasTexture | null {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return null;
  draw(context);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Soft round puff for steam, smoke or gas, drawn once at runtime. */
export function softPuffTexture(stage: LabStage): THREE.CanvasTexture | null {
  const texture = canvasTexture(128, 128, (context) => {
    const gradient = context.createRadialGradient(64, 64, 4, 64, 64, 62);
    gradient.addColorStop(0, 'rgba(255,255,255,0.95)');
    gradient.addColorStop(0.45, 'rgba(255,255,255,0.45)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, 128, 128);
  });
  return texture ? stage.keep(texture) : null;
}

export function createLabStage(
  renderer: THREE.WebGLRenderer,
  palette: LabPalette,
  reducedMotion: boolean,
  options: { cameraPosition?: [number, number, number]; target?: [number, number, number]; benchY?: number } = {},
): LabStage {
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;

  const disposables: Array<{ dispose: () => void }> = [];
  const keep = <T extends { dispose: () => void }>(item: T) => {
    disposables.push(item);
    return item;
  };

  const scene = new THREE.Scene();
  // Studio gradient from design tokens: deep blue-grey above (so steam and glass read), pale sky below.
  const skyTop = palette.liquid.clone().lerp(palette.metal, 0.55);
  const skyBottom = palette.ice.clone();
  const background = canvasTexture(4, 256, (context) => {
    const gradient = context.createLinearGradient(0, 0, 0, 256);
    gradient.addColorStop(0, `#${skyTop.getHexString()}`);
    gradient.addColorStop(0.27, `#${skyBottom.getHexString()}`);
    gradient.addColorStop(1, `#${skyBottom.getHexString()}`);
    context.fillStyle = gradient;
    context.fillRect(0, 0, 4, 256);
  });
  if (background) keep(background);
  scene.background = background ?? skyBottom;
  scene.fog = new THREE.Fog(skyBottom, 9, 19);
  const pmrem = keep(new THREE.PMREMGenerator(renderer));
  const room = keep(new RoomEnvironment());
  scene.environment = keep(pmrem.fromScene(room, 0.04).texture);
  scene.environmentIntensity = 0.95;

  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
  camera.position.set(...(options.cameraPosition ?? [0.6, 3.1, 12.6]));
  const controls = keep(new OrbitControls(camera, renderer.domElement));
  controls.target.set(...(options.target ?? [0, 0.45, 0]));
  controls.enablePan = false;
  controls.enableZoom = false; // keep page scrolling
  controls.enableDamping = true;
  controls.minPolarAngle = 0.55;
  controls.maxPolarAngle = 1.5;
  controls.autoRotateSpeed = 0.35;
  controls.update();

  const key = new THREE.DirectionalLight(0xffffff, 2.4);
  key.position.set(-4.5, 8, 5);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  Object.assign(key.shadow.camera, { left: -6, right: 6, top: 6, bottom: -6 });
  key.shadow.radius = 4;
  key.shadow.bias = -0.0005;
  const fill = new THREE.DirectionalLight(0xffffff, 0.6);
  fill.position.set(5, 3, -3);
  scene.add(key, fill);

  const benchY = options.benchY ?? -1.82;
  const bench = new THREE.Mesh(
    keep(new THREE.PlaneGeometry(80, 80)),
    keep(new THREE.MeshPhysicalMaterial({ color: palette.bench, roughness: 0.38, clearcoat: 0.25 })),
  );
  bench.rotation.x = -Math.PI / 2;
  bench.position.y = benchY;
  bench.receiveShadow = true;
  scene.add(bench);

  return {
    scene,
    camera,
    controls,
    renderer,
    palette,
    benchY,
    keep,
    finish: () => {
      scene.traverse((object) => {
        if (object === bench) return;
        if (object instanceof THREE.Mesh || object instanceof THREE.Sprite) {
          const list = Array.isArray(object.material) ? object.material : [object.material];
          list.forEach((material: THREE.Material) => {
            if ('fog' in material) (material as THREE.Material & { fog: boolean }).fog = false;
          });
        }
      });
    },
    render: (playing) => {
      controls.autoRotate = playing && !reducedMotion;
      controls.update();
      renderer.render(scene, camera);
    },
    resize: (width, height) => {
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    },
    dispose: () => disposables.forEach((item) => item.dispose()),
  };
}

/** Clear lab glass: reflective, slightly visible, does not hide what is inside. */
export function labGlassMaterial(stage: LabStage, opacity = 0.15): THREE.MeshPhysicalMaterial {
  return stage.keep(new THREE.MeshPhysicalMaterial({
    color: stage.palette.glass,
    metalness: 0,
    roughness: 0.02,
    transparent: true,
    opacity,
    ior: 1.5,
    clearcoat: 1,
    clearcoatRoughness: 0.02,
    specularIntensity: 1,
    envMapIntensity: 2.2,
    side: THREE.FrontSide,
    depthWrite: false,
  }));
}

/** Thicker glass at rims and bases, where real glassware is easiest to see. */
export function labGlassEdgeMaterial(stage: LabStage): THREE.MeshPhysicalMaterial {
  return stage.keep(new THREE.MeshPhysicalMaterial({
    color: stage.palette.glass, transparent: true, opacity: 0.55, roughness: 0.05, clearcoat: 1, envMapIntensity: 2.5, depthWrite: false,
  }));
}

/**
 * Refractive liquid. Refractive objects cannot see other refractive objects, so put only the
 * liquid (and ice) on transmission; make bubbles, crystals and glass transparent instead.
 */
export function refractiveLiquidMaterial(
  stage: LabStage,
  color: THREE.Color,
  options: { ior?: number; attenuationDistance?: number; tint?: number } = {},
): THREE.MeshPhysicalMaterial {
  return stage.keep(new THREE.MeshPhysicalMaterial({
    color: color.clone().lerp(new THREE.Color(0xffffff), options.tint ?? 0.3),
    metalness: 0,
    roughness: 0.03,
    transmission: 1,
    thickness: 0.9,
    ior: options.ior ?? 1.33,
    attenuationColor: color,
    attenuationDistance: options.attenuationDistance ?? 2.6,
    specularIntensity: 1,
    envMapIntensity: 1.3,
    side: THREE.DoubleSide,
  }));
}

export interface LabBeaker {
  group: THREE.Group;
  innerRadius: number;
  innerFloorY: number;
  rimY: number;
}

/** A lathe-built beaker with wall thickness, rolled rim, base edge and graduation marks. */
export function createBeaker(stage: LabStage, options: { radius?: number; height?: number; floorY?: number; marks?: number } = {}): LabBeaker {
  const R = options.radius ?? 1.45;
  const H = options.height ?? 3.2;
  const floorY = options.floorY ?? -1.26;
  const wall = 0.07;
  const points = [
    [0, 0], [R - 0.2, 0], [R - 0.06, 0.03], [R - 0.005, 0.12], [R, 0.2],
    [R, H], [R + 0.03, H + 0.03], [R + 0.05, H + 0.07], [R + 0.02, H + 0.1], [R - wall, H + 0.08],
    [R - wall, 0.2 + wall], [R - wall - 0.08, wall + 0.03], [R - 0.25, wall], [0, wall],
  ].map(([x, y]) => new THREE.Vector2(x, y));
  const group = new THREE.Group();
  const body = new THREE.Mesh(stage.keep(new THREE.LatheGeometry(points, 112)), labGlassMaterial(stage));
  body.position.y = floorY;
  body.renderOrder = 10;
  const edge = labGlassEdgeMaterial(stage);
  const rim = new THREE.Mesh(stage.keep(new THREE.TorusGeometry(R + 0.015, 0.045, 12, 112)), edge);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = floorY + H + 0.06;
  const base = new THREE.Mesh(stage.keep(new THREE.TorusGeometry(R - 0.05, 0.05, 12, 112)), edge);
  base.rotation.x = Math.PI / 2;
  base.position.y = floorY + 0.07;
  rim.renderOrder = 11;
  base.renderOrder = 11;
  group.add(body, rim, base);
  const markMaterial = stage.keep(new THREE.MeshBasicMaterial({ color: stage.palette.glass, transparent: true, opacity: 0.85 }));
  const markGeometry = stage.keep(new THREE.BoxGeometry(1, 0.03, 0.012));
  const marks = options.marks ?? 6;
  for (let mark = 1; mark <= marks; mark += 1) {
    const tick = new THREE.Mesh(markGeometry, markMaterial);
    const angle = -2.15;
    tick.scale.x = mark % 2 === 0 ? 0.42 : 0.24;
    tick.position.set(Math.cos(angle) * (R + 0.006), floorY + 0.25 + (mark * (H - 0.7)) / marks, -Math.sin(angle) * (R + 0.006));
    tick.lookAt(tick.position.x * 2, tick.position.y, tick.position.z * 2);
    tick.rotateY(Math.PI / 2);
    tick.renderOrder = 11;
    group.add(tick);
  }
  stage.scene.add(group);
  return { group, innerRadius: R - wall - 0.012, innerFloorY: floorY + wall, rimY: floorY + H };
}

export interface LabHotPlate {
  group: THREE.Group;
  /** 0 = off, 1 = hottest: coil and ceramic glow from heat to glow colour, plus a warm light. */
  setHeat: (heat: number) => void;
}

/** Hot plate under glassware whose base sits at `floorY`. */
export function createHotPlate(stage: LabStage, floorY = -1.26): LabHotPlate {
  const { palette, keep } = stage;
  const group = new THREE.Group();
  const body = new THREE.Mesh(
    keep(new THREE.CylinderGeometry(2.05, 2.15, 0.5, 64)),
    keep(new THREE.MeshPhysicalMaterial({ color: palette.metal, metalness: 0.7, roughness: 0.32, clearcoat: 0.5 })),
  );
  body.position.y = floorY - 0.31;
  body.castShadow = true;
  body.receiveShadow = true;
  const ceramicMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal, roughness: 0.12, clearcoat: 1, emissive: palette.heat, emissiveIntensity: 0 }));
  const ceramic = new THREE.Mesh(keep(new THREE.CylinderGeometry(1.92, 1.92, 0.05, 64)), ceramicMaterial);
  ceramic.position.y = floorY - 0.035;
  ceramic.receiveShadow = true;
  const coilMaterial = keep(new THREE.MeshStandardMaterial({ color: palette.metal, emissive: palette.heat, emissiveIntensity: 0, roughness: 0.5 }));
  const coil = new THREE.Mesh(keep(new THREE.TorusGeometry(1.72, 0.035, 8, 96)), coilMaterial);
  coil.rotation.x = Math.PI / 2;
  coil.position.y = floorY;
  const knob = new THREE.Mesh(
    keep(new THREE.CylinderGeometry(0.16, 0.16, 0.14, 32)),
    keep(new THREE.MeshPhysicalMaterial({ color: palette.heat, roughness: 0.3, clearcoat: 1 })),
  );
  knob.rotation.x = Math.PI / 2;
  knob.position.set(0, floorY - 0.32, 2.13);
  const light = new THREE.PointLight(palette.heat, 0, 5, 1.6);
  light.position.set(0, floorY + 0.3, 0);
  group.add(body, ceramic, coil, knob, light);
  stage.scene.add(group);
  const cool = palette.heat.clone();
  const hot = palette.glow.clone();
  return {
    group,
    setHeat: (heat) => {
      const h = THREE.MathUtils.clamp(heat, 0, 1);
      coilMaterial.emissive.copy(cool).lerp(hot, h * h);
      coilMaterial.emissiveIntensity = 0.15 + h * 3.2;
      ceramicMaterial.emissive.copy(coilMaterial.emissive);
      ceramicMaterial.emissiveIntensity = h * h * 0.55;
      light.intensity = h * 9;
    },
  };
}

export interface RippleSurface {
  mesh: THREE.Mesh;
  /** amplitude in world units; churn adds a boiling pattern. Call once per frame. */
  update: (amplitude: number, t: number, churn: boolean) => void;
}

/** A polar-grid liquid surface whose interior vertices can ripple; the rim stays still. */
export function createRippleSurface(stage: LabStage, radius: number, material: THREE.Material, rings = 22, segments = 80): RippleSurface {
  const count = 1 + rings * segments;
  const positions = new Float32Array(count * 3);
  const radii = new Float32Array(count);
  const angles = new Float32Array(count);
  for (let ring = 1; ring <= rings; ring += 1) {
    for (let segment = 0; segment < segments; segment += 1) {
      const index = 1 + (ring - 1) * segments + segment;
      const r = (ring / rings) * radius;
      const angle = (segment / segments) * Math.PI * 2;
      radii[index] = r;
      angles[index] = angle;
      positions[index * 3] = Math.cos(angle) * r;
      positions[index * 3 + 2] = Math.sin(angle) * r;
    }
  }
  const indices: number[] = [];
  for (let segment = 0; segment < segments; segment += 1) indices.push(0, 1 + ((segment + 1) % segments), 1 + segment);
  for (let ring = 1; ring < rings; ring += 1) {
    for (let segment = 0; segment < segments; segment += 1) {
      const next = (segment + 1) % segments;
      const a = 1 + (ring - 1) * segments + segment;
      const b = 1 + (ring - 1) * segments + next;
      const c = 1 + ring * segments + segment;
      const d = 1 + ring * segments + next;
      indices.push(a, b, c, b, d, c);
    }
  }
  const geometry = stage.keep(new THREE.BufferGeometry());
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(geometry, material);
  stage.scene.add(mesh);
  const attribute = geometry.getAttribute('position') as THREE.BufferAttribute;
  return {
    mesh,
    update: (amplitude, t, churn) => {
      for (let index = 0; index < attribute.count; index += 1) {
        const r = radii[index];
        const angle = angles[index];
        const edge = 1 - Math.pow(r / radius, 6);
        const wave = Math.sin(r * 7 - t * 3.2) * 0.6 + Math.sin(angle * 3 + r * 4 + t * 2.1) * 0.4;
        const boil = churn ? Math.sin(angle * 7 - t * 9 + r * 11) * Math.sin(r * 9 + t * 6) : 0;
        attribute.setY(index, (wave + boil) * amplitude * edge);
      }
      attribute.needsUpdate = true;
      geometry.computeVertexNormals();
    },
  };
}
